import { useState } from 'react';
import { App, Alert, Button, Card, Form, Input, Modal, Popconfirm, Space, Switch, Table, Tag, Typography } from 'antd';
import { GlobalOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons';
import type { DomainItem } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { baseURL, errorMessage } from '../../api/client';
import { EmptyState, tableLocale, tableLoading } from '../../components/states';
import { plural } from '../../utils/format';
import { NameCell } from '../../components/NameCell';

/** Public URL of the Resend webhook: the API base may be relative (same origin) or on another host. */
const WEBHOOK_URL = `${new URL(baseURL, window.location.origin).href.replace(/\/$/, '')}/webhooks/resend`;

const VERIFY_TAG = { verified: { color: 'success', label: 'Verified' }, pending: { color: 'warning', label: 'Pending' }, unsynced: { color: 'default', label: 'Not synced' } } as const;
import { useCreateDomain, useDeleteDomain, useDomains, useSyncDomains, useUpdateDomain } from '../../features/email/api';

export function DomainsPage() {
  const { message } = App.useApp();
  const domains = useDomains();
  const create = useCreateDomain();
  const update = useUpdateDomain();
  const remove = useDeleteDomain();
  const sync = useSyncDomains();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<{ domain: string; inboundEnabled: boolean }>();

  return (
    <div className="page">
      <PageHeader
        title="Email domains"
        subtitle="Domains your agents send from and receive replies on. Verify each one in Resend (DNS), then enable receiving."
        extra={
          <Space>
            <Button icon={<SyncOutlined />} loading={sync.isPending} onClick={() => sync.mutateAsync().then((r) => message.success(`Synced ${plural(r.total, 'domain')} from Resend: ${r.created} new, ${r.updated} updated`)).catch((e) => message.error(errorMessage(e)))}>
              Sync with Resend
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setOpen(true); }}>
              Add domain
            </Button>
          </Space>
        }
      />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        title="Inbound mail"
        description={<span>Point Resend's webhook at <Typography.Text code>{WEBHOOK_URL}</Typography.Text> with the email events and <b>email.received</b> enabled, and turn on receiving for the domain in Resend. Replies then land in the Inbox under their domain.</span>}
      />
      <Card>
      <Table<DomainItem>
        rowKey="id"
        loading={tableLoading(domains)}
        locale={tableLocale(
          domains,
          <EmptyState description="No domains yet. Add one, or sync the domains already in your Resend account." action={<Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setOpen(true); }}>Add domain</Button>} />,
        )}
        dataSource={domains.data ?? []}
        pagination={false}
        columns={[
          { title: 'Domain', dataIndex: 'domain', render: (v: string, d) => <NameCell name={v} icon={<GlobalOutlined />} sub={d.inboundEnabled ? 'Sending and receiving' : 'Sending only'} /> },
          { title: 'Verified in Resend', dataIndex: 'verified', width: 170, render: (v: boolean, d) => {
              const t = VERIFY_TAG[v ? 'verified' : d.resendDomainId ? 'pending' : 'unsynced'];
              return <Tag color={t.color}>{t.label}</Tag>;
            } },
          { title: 'Receiving', dataIndex: 'inboundEnabled', width: 120, render: (v: boolean, d) => <Switch size="small" checked={v} onChange={(c) => update.mutateAsync({ id: d.id, inboundEnabled: c }).catch((e) => message.error(errorMessage(e)))} /> },
          { title: 'Agents', dataIndex: 'agentCount', width: 90 },
          { title: 'Unread', dataIndex: 'unreadCount', width: 90, render: (v) => v ?? 0 },
          {
            key: 'actions',
            align: 'right',
            render: (_, d) => (
              <Popconfirm title={`Remove ${d.domain}?`} okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => remove.mutateAsync(d.id).then(() => message.success('Domain removed')).catch((e) => message.error(errorMessage(e)))}>
                <Button size="small" danger>Remove</Button>
              </Popconfirm>
            ),
          },
        ]}
      />
      </Card>
      <Modal title="Add email domain" open={open} onCancel={() => setOpen(false)} onOk={() => form.submit()} okText="Add" confirmLoading={create.isPending} destroyOnHidden>
        <Form form={form} layout="vertical" requiredMark={false} initialValues={{ inboundEnabled: true }} onFinish={(v) => create.mutateAsync(v).then(() => setOpen(false)).catch((e) => message.error(errorMessage(e)))}>
          <Form.Item name="domain" label="Domain" rules={[{ required: true }]} extra="A domain already added and verified in your Resend account, e.g. mail.yourcompany.com">
            <Input autoFocus placeholder="mail.yourcompany.com" />
          </Form.Item>
          <Form.Item name="inboundEnabled" label="Receive replies on this domain" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
